import React, { RefObject, useEffect, useMemo, useRef, useState } from "react";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import { arrowDownIcon, arrowUpIcon } from "../../assets/icons/SvgIconList";
import { cn } from "../../lib";
import { BIT_VALUE } from "../../utils/AppEnumerations";
import { IOption } from "../../data/AppInterface";

export interface ICustomDropdownProps<T, K> {
  options: IOption<T, K>[];
  onSelect: (option: IOption<T, K>) => void;
  className?: string;
  selected?: T;
  searchable?: boolean;
  disabled?: boolean;
  label?: string;
  errorMessage?: any;
  defaultOption?: IOption<T>;
  required?: boolean;
  placeholder?: string;
}

const ICDropDown = <T extends Object, K = undefined>({
  options,
  onSelect,
  className,
  selected,
  searchable,
  disabled,
  label,
  errorMessage,
  defaultOption,
  required,
  placeholder,
}: ICustomDropdownProps<T, K>) => {
  const { t } = UseTranslationHook();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [searchText, setSearchText] = useState("");
  const [contentTopDown, setContentTopDown] = useState(false);
  const wrapperRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const handleOutsideClick = (event: MouseEvent) => {
    if (
      wrapperRef.current &&
      !wrapperRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };
  useEffect(() => {
    const handleResize = () => {
      if (wrapperRef.current) {
        const contentRect = wrapperRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const remainingSpace = viewportHeight - contentRect.bottom;
        if (remainingSpace < 250) {
          setContentTopDown(true);
        } else {
          setContentTopDown(false);
        }
      }
    };
    document.addEventListener("mousedown", (event) => {
      handleOutsideClick(event);
      handleResize();
    });

    return () => {
      document.removeEventListener("mousedown", (event) => {
        handleOutsideClick(event);
        handleResize();
      });
    };
  }, [wrapperRef?.current?.getBoundingClientRect()]);

  useEffect(() => {
    if (!isOpen) {
      setSearchText("");
    }
  }, [isOpen]);

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };
  const getSelectedOption = () => {
    const item = [
      ...(defaultOption
        ? [defaultOption]
        : [{ display_value: "", data_value: "" }]),
      ...options,
    ].find((item) => item.data_value === selected);
    if (item) {
      return t(item.display_value);
    }
    return "";
  };

  const filteredOptions = useMemo(() => {
    return options.filter((item) =>
      item?.display_value?.toLowerCase().includes(searchText?.toLowerCase())
    );
  }, [searchText, options]);

  return (
    <div>
      <div
        className={cn(`relative w-full h-10`, { [`${className}`]: className })}
        ref={wrapperRef}
      >
        <div
          onClick={toggleDropdown}
          className={cn(
            `relative w-full p-2 h-full border border-muted text-muted rounded-lg  text-left dropdown-button hover:border-black`,
            {
              open: isOpen,
              "error-border": errorMessage,
              "cursor-pointer": !disabled,
            }
          )}
        >
          {label && (
            <button
              className={cn(
                `absolute left-2 top-0 transition-all duration-300`,
                {
                  "flex items-center h-full": getSelectedOption() === "",
                  "-translate-y-[60%] pointer-events-none bg-subtle px-1 text-xs":
                    getSelectedOption(),
                }
              )}
            >
              {label + (required ? "*" : "")}
            </button>
          )}
          <div
            className={cn("text-base overflow-hidden", {
              "text-default": getSelectedOption(),
              "text-disable": disabled,
            })}
          >
            {getSelectedOption() || placeholder}
          </div>
          {isOpen ? (
            <span className="absolute top-1/2 right-3 transform -translate-y-1/2 transition-transform duration-300 size-5">
              {arrowUpIcon}
            </span>
          ) : (
            <span className="absolute top-1/2 right-3 transform -translate-y-1/2 transition-transform duration-300 size-5">
              {arrowDownIcon}
            </span>
          )}
        </div>
        {isOpen && (
          <div
            className="absolute left-0 w-full max-h-[200px] overflow-y-auto z-10 bg-subtle rounded-b-lg scrollbar-hide shadow-2xl"
            style={{
              top: contentTopDown ? "" : "100%",
              bottom: contentTopDown ? "100%" : "",
              borderTop: contentTopDown ? "" : "0px",
              borderBottom: contentTopDown ? "0px" : "",
            }}
          >
            {searchable && isOpen && (
              <input
                type="text"
                className="h-10 w-full p-2 border-none border-t-0 outline-none"
                placeholder={t("search") + "..."}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                autoFocus
              />
            )}

            {[
              ...(defaultOption
                ? [defaultOption]
                : [{ display_value: "", data_value: "" }]),
              ...filteredOptions,
            ].map((option, index) => (
              <div
                key={option.data_value as string}
                className={cn(`p-2 cursor-pointer`, {
                  "bg-danger-muted":
                    (option as any).is_deleted === BIT_VALUE.One,
                })}
                onClick={() => {
                  onSelect(option as IOption<T, K>);
                  toggleDropdown();
                }}
              >
                {t(option.display_value)}
              </div>
            ))}
          </div>
        )}
      </div>
      {errorMessage && (
        <div className="text-sm text-danger">{errorMessage}</div>
      )}
    </div>
  );
};

export default ICDropDown;
